import {
  Component,
  Input,
  Output,
  EventEmitter,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { tap } from 'rxjs';
import { DateUtilsService } from '../../services/date-utils/date-utils.service';
import {
  MatDatepicker,
  MatDateRangePicker,
} from '@angular/material/datepicker';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MomentDateAdapter,
} from '@angular/material-moment-adapter';
import moment from 'moment-timezone';

export const CUSTOM_FORMAT = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'DD/MM/YYYY',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'app-date-picker-input',
  templateUrl: './date-picker-input.component.html',
  styleUrls: ['./date-picker-input.component.scss'],
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    { provide: MAT_DATE_FORMATS, useValue: CUSTOM_FORMAT },
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { strict: true } },
  ],
})
export class DatePickerInputComponent {
  @Input() singleDateMode: boolean = false;
  @Input() label: string = '';
  @Input() start: string = '';
  @Input() end: string = '';
  @Input() required: boolean = false;
  @Input() customError: string = '';
  @Input() hideErrors: boolean = true;
  /** Somente leitura: o valor continua visível, mas não é editável nem pelo picker. */
  @Input() disabled: boolean = false;
  @Output() startChange = new EventEmitter<any>();
  @Output() endChange = new EventEmitter<any>();
  @Output() isDateValid = new EventEmitter<boolean>();
  @Output() pickerClosed = new EventEmitter<void>();
  @ViewChild('picker') picker:
    | MatDatepicker<Date>
    | MatDateRangePicker<Date>
    | undefined;

  range: FormGroup;
  dataInicio: string = '';
  dataFinal: string = '';
  inputFocused = false;

  constructor(private dateUtils: DateUtilsService) {
    this.range = new FormGroup({
      start: new FormControl<Date | null>(null),
      end: new FormControl<Date | null>(null),
    });

    this.range.valueChanges.subscribe({
      next: (value: any) => {
        const start = this.dateUtils.formatDateToISO8601(value.start);
        const end = this.dateUtils.formatDateToISO8601(value.end);
        const hasValidStart =
          start !== '' && this.dateUtils.isValidDateString(start);
        const hasValidEnd = end !== '' && this.dateUtils.isValidDateString(end);

        if (this.dataInicio !== start) {
          this.dataInicio = start;
          if (hasValidStart) {
            this.startChange.emit(value.start);
          }
        }

        if (this.dataFinal !== end) {
          this.dataFinal = end;
          if (hasValidEnd) {
            this.endChange.emit(value.end);
          }
        }

        this.emitValidityStatus();
      },
      error: (error) => {
        console.error(error);
      },
    });
  }

  ngOnInit(): void {
    if (this.required) {
      this.range.get('start')?.setValidators(Validators.required);
      this.range.get('end')?.setValidators(Validators.required);
    }

    if (this.start) {
      this.range.patchValue({ start: moment(this.start, 'YYYY-MM-DD') });
    }
    if (this.end) {
      this.range.patchValue({ end: moment(this.end, 'YYYY-MM-DD') });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['start'] && !changes['start'].firstChange) {
      this.updateRangeFromInputs();
      this.emitValidityStatus();
    }

    if (changes['end'] && !changes['end'].firstChange) {
      this.updateRangeFromInputs();
      this.emitValidityStatus();
    }
  }

  openPicker() {
    if (this.disabled) return;
    if (this.inputFocused) return;
    this.inputFocused = true;
    if (this.picker) {
      this.picker.open();
    }
  }

  closePicker() {
    this.inputFocused = false;

    if (this.picker && this.picker.opened) {
      this.picker.close();
    }
  }

  onPickerClosed() {
    this.inputFocused = false;
    this.pickerClosed.emit();
  }

  onDateInput(event: Event) {
    const input = event.target as HTMLInputElement;

    let trimmed = input.value.replace(/\D/g, '');
    if (trimmed.length > 8) {
      trimmed = trimmed.substring(0, 8);
    }

    let formatted = trimmed;
    if (trimmed.length > 4) {
      formatted = trimmed.replace(/^(\d{2})(\d{2})(\d{1,4}).*/, '$1/$2/$3');
    } else if (trimmed.length > 2) {
      formatted = trimmed.replace(/^(\d{2})(\d{1,2}).*/, '$1/$2');
    }

    input.value = formatted;
  }

  executeBlurValidation(
    controlName: 'start' | 'end',
    inputElement: HTMLInputElement,
  ) {
    if (this.disabled) return;

    const control = this.range.get(controlName);
    const rawText = inputElement.value;

    if (!control) return;

    if (!rawText) {
      control.setValue(null);
      control.markAsTouched();
      this.emitValidityStatus();

      if (controlName === 'start') {
        this.dataInicio = '';
        this.emitStartChangeIfPossible('');
      }

      if (controlName === 'end') {
        this.dataFinal = '';
        this.emitEndChangeIfPossible('');
      }
      return;
    }

    const isInvalid =
      !control.value ||
      (moment.isMoment(control.value) && !control.value.isValid()) ||
      rawText.length < 10;

    if (isInvalid) {
      control.setValue(null);
      inputElement.value = '';
      control.markAsTouched();
    }

    this.emitValidityStatus();

    if (controlName === 'start') {
      this.dataInicio = this.dateUtils.convertDateStringFormat(rawText);
      this.emitStartChangeIfPossible(this.dataInicio);
    }

    if (controlName === 'end') {
      this.dataFinal = this.dateUtils.convertDateStringFormat(rawText);
      this.emitEndChangeIfPossible(this.dataFinal);
    }
  }

  private emitValidityStatus() {
    const form = this.range.value;
    const start = this.dateUtils.formatDateToISO8601(form.start);
    const end = this.dateUtils.formatDateToISO8601(form.end);
    let isStartValid = false;
    let isEndValid = false;
    let isBothValid = false;

    if (this.singleDateMode) {
      isStartValid =
        (this.range.get('start')?.valid || false) &&
        this.dateUtils.isValidDateString(start);

      return this.isDateValid.emit(isStartValid);
    }

    if (!this.singleDateMode) {
      isStartValid =
        (this.range.get('start')?.valid || false) &&
        this.dateUtils.isValidDateString(start);
      isEndValid =
        (this.range.get('end')?.valid || false) &&
        this.dateUtils.isValidDateString(end);
      isBothValid = isStartValid && isEndValid;

      return this.isDateValid.emit(isBothValid);
    }
  }

  private updateRangeFromInputs(): void {
    let start = this.start;
    let end = this.end;

    if (moment.isMoment(this.start)) {
      start = this.start.format('YYYY-MM-DD');
    }

    if (moment.isMoment(this.end)) {
      end = this.end.format('YYYY-MM-DD');
    }

    this.range.patchValue(
      {
        start: start ? start : null,
        end: end ? end : null,
      },
      { emitEvent: false },
    );
  }

  private emitStartChangeIfPossible(dataObj: any) {
    if (this.dataInicio && this.dateUtils.isValidDateString(this.dataInicio)) {
      return this.startChange.emit(dataObj);
    }

    if (this.dataInicio === '' && this.range.get('start')?.touched) {
      return this.startChange.emit('');
    }
  }

  private emitEndChangeIfPossible(dataObj: any) {
    if (this.dataFinal && this.dateUtils.isValidDateString(this.dataFinal)) {
      return this.endChange.emit(dataObj);
    }

    if (this.dataFinal === '' && this.range.get('end')?.touched) {
      return this.endChange.emit('');
    }
  }
}
